import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Wand2, AlertTriangle, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";

import { GET_THEORY_OVERVIEW, GET_THEORY_PREVIEW } from "@/graphql/queries/admin";
import {
  ContentFilterBar,
  useContentCatalog,
  useContentFilter,
  type ContentCatalog,
  type ContentFilter,
} from "@/components/admin/content-filters";
import { TheoryView } from "@/components/theory-view";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";


interface ConceptLevelCount {
  level: number;
  count: number;
}

interface ConceptCoverage {
  key: string;
  name: string;
  description?: string | null;
  /** Null when the theory carries no tags at all — no information, not a gap. */
  theoryBlocks: number | null;
  hasExample: boolean;
  hasIntro: boolean;
  exerciseCounts: ConceptLevelCount[];
  exerciseTotal: number;
}

interface LevelCoverage {
  level: number;
  questions: number;
  tagged: number;
  untagged: number;
}

interface TheoryPreview {
  subject: string;
  grade: number;
  skillKey: string;
  skillName: string;
  theory: string | null;
  hasTheory: boolean;
  tagged: boolean;
  concepts: ConceptCoverage[];
  levels: LevelCoverage[];
}

interface OverviewRow {
  skillKey: string;
  skillName: string;
  subject: string;
  grade: number;
  hasTheory: boolean;
  tagged: boolean;
  conceptCount: number;
  questionCount: number;
  untaggedQuestions: number;
  unexplainedConcepts: number;
  conceptsWithoutExample: number;
  unpractisedConcepts: number;
  conceptsWithoutIntro: number;
}

export default function TheoryPreviewPage() {
  const catalog = useContentCatalog();
  const { filter, setFilter, clearFilter } = useContentFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  // Coverage is what makes the page more than a preview, so it starts on. Off is
  // for reading the theory as a child reads it.
  const showCoverage = searchParams.get("coverage") !== "0";

  const setCoverage = (on: boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("coverage", on ? "1" : "0");
        return next;
      },
      { replace: true },
    );

  // One skill is a theory to read; anything else is the list. Picking a second
  // skill therefore goes back to the list rather than choosing between them —
  // „two skills" is a question about which of them needs attention.
  const single = filter.skills.length === 1 ? filter.skills[0] : undefined;
  const singleSkill = catalog.skills.find((skill) => skill.id === single);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Teorija</h1>
          <p className="text-sm text-muted-foreground">
            Kurią teorijos dalį kuri sąvoka moko, ir kiek užduočių tą sąvoką treniruoja
          </p>
        </div>
      </div>

      {/* The filters stay put in both views, so „atgal" from a theory lands on the
          list it was opened from. Every one of them is independent: a grade with no
          subject, two subjects with no grade, or nothing at all. */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <ContentFilterBar
            catalog={catalog}
            filter={filter}
            setFilter={setFilter}
            clearFilter={clearFilter}
          />
        </CardContent>
      </Card>

      {/* Nothing is rendered under the pickers until the catalog is in: the skill is
          what says which subject and grade this theory belongs to, and guessing
          would fire the query for the wrong file and flash „nėra teorijos". */}
      {catalog.loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : single ? (
        <TheoryDetail
          subject={singleSkill?.subject || filter.subjects[0] || "math"}
          grade={singleSkill?.grade ?? filter.grades[0] ?? 1}
          skill={single}
          filter={filter}
          showCoverage={showCoverage}
          onToggleCoverage={setCoverage}
          onBack={() => setFilter({ skills: [], concepts: [] })}
        />
      ) : (
        <TheoryList
          catalog={catalog}
          filter={filter}
          onOpen={(row) => setFilter({ skills: [row.skillKey], concepts: [] })}
        />
      )}
    </div>
  );
}

// ─── The list ───

/**
 * Every skill's theory in one table, worst first.
 *
 * The list exists because the gaps are not something you go looking for skill by
 * skill — a theory that explains four of its five concepts looks fine until it is
 * next to one that explains all five. Ordering by how much is wrong puts the work
 * at the top; the HTML is fetched only for the one that gets opened.
 */
function TheoryList({
  catalog,
  filter,
  onOpen,
}: {
  catalog: ContentCatalog;
  filter: ContentFilter;
  onOpen: (row: OverviewRow) => void;
}) {
  // Both filters go to the server as lists, and an empty one means "every" — the
  // rows come from `config/skills.yml`, so a skill with no theory at all is in the
  // list rather than absent from it, which is the biggest gap there is.
  const { data, loading } = useQuery(GET_THEORY_OVERVIEW, {
    variables: { subjects: filter.subjects, grades: filter.grades },
  });

  const rows: OverviewRow[] = data?.theoryOverview || [];

  // Skill, level and concept are applied here rather than by the server: the row
  // is per skill, and „which skills teach this concept" is answerable from the
  // catalog without another query.
  const filtered = useMemo(() => {
    const byConcept = new Set(
      catalog.skills
        .filter((skill) =>
          skill.concepts.some((concept) => filter.concepts.includes(concept.key)),
        )
        .map((skill) => skill.id),
    );
    const byLevel = new Set(
      catalog.skills
        .filter((skill) =>
          skill.concepts.some(
            (concept) =>
              // No declared levels means all three (ConceptTarget) — never none,
              // which would hide every skill nobody has graded yet.
              !concept.levels?.length ||
              concept.levels.some((level) => filter.levels.includes(level)),
          ),
        )
        .map((skill) => skill.id),
    );

    return rows
      .filter((row) => filter.skills.length === 0 || filter.skills.includes(row.skillKey))
      .filter((row) => filter.concepts.length === 0 || byConcept.has(row.skillKey))
      .filter((row) => filter.levels.length === 0 || byLevel.has(row.skillKey));
  }, [rows, catalog.skills, filter]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => problems(b) - problems(a)),
    [filtered],
  );

  const subjectNames = useMemo(
    () => new Map(catalog.subjects.map((subject) => [subject.id, subject.name])),
    [catalog.subjects],
  );
  // Shown only where the list actually spans them, or every row carries a label
  // saying what the filter above already says.
  const manySubjects = new Set(sorted.map((row) => row.subject)).size > 1;
  const manyGrades = new Set(sorted.map((row) => row.grade)).size > 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Pagal pasirinktus filtrus įgūdžių nėra.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="py-0 overflow-hidden">
      <CardContent className="p-0">
        <div className="grid grid-cols-[1fr_auto] items-center gap-2 px-4 py-2 border-b bg-muted/30 text-xs text-muted-foreground">
          <span>Tema ({sorted.length})</span>
          <span>Būklė</span>
        </div>
        <div className="divide-y">
          {sorted.map((row) => (
            <button
              key={row.skillKey}
              type="button"
              onClick={() => onOpen(row)}
              className="w-full text-left px-4 py-2.5 hover:bg-muted/40 cursor-pointer grid grid-cols-[1fr_auto] items-center gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{row.skillName}</span>
                  {manySubjects && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {subjectNames.get(row.subject) || row.subject}
                    </Badge>
                  )}
                  {manyGrades && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {row.grade} kl.
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.conceptCount > 0
                    ? `${row.conceptCount} sąvokos · `
                    : ""}
                  {row.questionCount} užd.
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <RowStatus row={row} />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * How much is wrong with one skill's theory, as a number to sort by. Weighted, not
 * counted: no theory at all outranks any number of smaller holes, and an untagged
 * skill is not "fine" — it is unknown, which sorts above known-good.
 */
function problems(row: OverviewRow): number {
  if (!row.hasTheory) return 1000 + row.questionCount;

  return (
    row.unexplainedConcepts * 20 +
    row.conceptsWithoutExample * 10 +
    row.unpractisedConcepts * 5 +
    // Weighted below the others on purpose: a concept with no written
    // introduction still gets one, just the weaker derived kind, so it is a gap
    // to fill rather than a hole a child falls into. It still has to be here —
    // it is the only gap no screen, flag or failing session can surface.
    row.conceptsWithoutIntro * 2 +
    (row.conceptCount === 0 ? 3 : 0) +
    (row.tagged ? 0 : 2)
  );
}

function RowStatus({ row }: { row: OverviewRow }) {
  if (!row.hasTheory) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] text-red-600 dark:text-red-400 border-red-400/50"
        title="Ši tema teorijos neturi — „Parodyk pavyzdį“ neturi ko parodyti"
      >
        nėra teorijos
      </Badge>
    );
  }

  const flags: { label: string; title: string }[] = [];

  if (row.conceptCount === 0) {
    flags.push({
      label: "be sąvokų",
      title: "Nėra concepts.json — sugeneruok teoriją, ir sąrašas atsiras iš jos",
    });
  } else if (!row.tagged) {
    flags.push({
      label: "be žymių",
      title:
        "Teorija parašyta prieš žymėjimą, todėl nežinome, kuri jos dalis ką moko. " +
        "Sugeneruok iš naujo.",
    });
  }
  if (row.unexplainedConcepts > 0) {
    flags.push({
      label: `${row.unexplainedConcepts} be paaiškinimo`,
      title: "Sąvokų, kurių teorija nepaaiškina, nors užduočių yra",
    });
  }
  if (row.conceptsWithoutExample > 0) {
    flags.push({
      label: `${row.conceptsWithoutExample} be pavyzdžio`,
      title: "Sąvokų be išspręsto pavyzdžio — užstrigęs vaikas gaus svetimą pavyzdį",
    });
  }
  if (row.unpractisedConcepts > 0) {
    flags.push({
      label: `${row.unpractisedConcepts} be užduočių`,
      title: "Sąvokų, kurių niekas netreniruoja",
    });
  }
  if (row.conceptsWithoutIntro > 0) {
    flags.push({
      label: `${row.conceptsWithoutIntro} be įvado`,
      title:
        "Sąvokų be parašyto įvadinio klausimo. Vaikas vis tiek gauna įvadą, tik " +
        "silpnesnį — iš teorijos ištrauktą sakinį su klausimu „apie kurią sąvoką?“. " +
        "Užpildoma: rake generate:concept_intros SKILL=…",
    });
  }

  if (flags.length === 0) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] text-green-600 dark:text-green-400 border-green-400/50"
      >
        viskas padengta
      </Badge>
    );
  }

  return (
    <>
      {flags.map((flag) => (
        <Badge
          key={flag.label}
          variant="outline"
          className="text-[10px] text-amber-600 dark:text-amber-400 border-amber-400/50"
          title={flag.title}
        >
          {flag.label}
        </Badge>
      ))}
    </>
  );
}

// ─── One theory ───

/**
 * Label every tagged block with what it teaches.
 *
 * Only attributes are touched — the markup and the markdown inside it are left
 * exactly as stored, because TheoryView still has to render them the way a child
 * sees them. The label itself is drawn by CSS (`.theory-tags [data-concept-label]`
 * in globals.css), so nothing is injected into the prose.
 */
function annotateTags(html: string, label: (key: string) => string): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const el of Array.from(doc.querySelectorAll("[data-concept]"))) {
    const keys = (el.getAttribute("data-concept") || "").split(/\s+/).filter(Boolean);
    if (keys.length === 0) continue;

    el.setAttribute("data-concept-label", keys.map(label).join(" · "));
  }

  return doc.body.innerHTML;
}

function TheoryDetail({
  subject,
  grade,
  skill,
  filter,
  showCoverage,
  onToggleCoverage,
  onBack,
}: {
  subject: string;
  grade: number;
  skill: string;
  /** Concepts and levels narrow what the panels report; subject, grade and skill
   *  are what chose this theory. */
  filter: ContentFilter;
  showCoverage: boolean;
  onToggleCoverage: (on: boolean) => void;
  onBack: () => void;
}) {
  const { data, loading } = useQuery(GET_THEORY_PREVIEW, {
    variables: { subject, grade, skill },
  });
  const raw: TheoryPreview | undefined = data?.theoryPreview;

  // Filtering the *panels* and never the theory: the prose is what a child reads
  // and it is shown whole, tags and all. What narrows is the accounting beside it
  // — which concepts to report on, and which levels to count.
  //
  // Two steps, because the two filters do different things. A level filter changes
  // the *numbers*, and so has to reach the labels drawn on the theory itself; a
  // concept filter changes which concepts are *listed* and must not touch a count.
  const levelScoped: ConceptCoverage[] = useMemo(() => {
    if (!raw) return [];
    if (filter.levels.length === 0) return raw.concepts;

    return raw.concepts.map((concept) => {
      const counts = concept.exerciseCounts.filter((entry) =>
        filter.levels.includes(entry.level),
      );
      return {
        ...concept,
        exerciseCounts: counts,
        exerciseTotal: counts.reduce((sum, entry) => sum + entry.count, 0),
      };
    });
  }, [raw, filter.levels]);

  const preview: TheoryPreview | undefined = useMemo(() => {
    if (!raw) return undefined;

    return {
      ...raw,
      levels: filter.levels.length
        ? raw.levels.filter((level) => filter.levels.includes(level.level))
        : raw.levels,
      concepts: filter.concepts.length
        ? levelScoped.filter((concept) => filter.concepts.includes(concept.key))
        : levelScoped,
    };
  }, [raw, levelScoped, filter.levels, filter.concepts]);

  const conceptsByKey = useMemo(() => {
    const map = new Map<string, ConceptCoverage>();
    // Every concept the skill has, never the filtered subset: a tag on the theory
    // whose concept is merely filtered out is still a real concept, and labelling
    // it „nebėra sąraše" would invent a defect.
    levelScoped.forEach((concept) => map.set(concept.key, concept));
    return map;
  }, [levelScoped]);

  const annotated = useMemo(() => {
    if (!preview?.theory) return "";
    return annotateTags(preview.theory, (key) => {
      const concept = conceptsByKey.get(key);
      if (!concept) return `${key} (nebėra sąraše)`;
      if (!showCoverage) return concept.name;

      return `${concept.name} · ${concept.exerciseTotal} užd.`;
    });
  }, [preview, conceptsByKey, showCoverage]);

  const generateHref = `/admin/theory/generate?skill=${encodeURIComponent(
    skill,
  )}&name=${encodeURIComponent(preview?.skillName || skill)}&subject=${subject}&grade=${
    preview?.grade ?? grade
  }`;

  const hasSidePanel = (preview?.concepts.length || 0) > 0 || showCoverage;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Visos temos
          </Button>
          <h2 className="text-lg font-semibold">{preview?.skillName || skill}</h2>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={showCoverage}
              onCheckedChange={(checked) => onToggleCoverage(Boolean(checked))}
            />
            Užduočių aprėptis
          </label>
          <Button asChild variant="outline" size="sm">
            <Link to={generateHref}>
              <Wand2 className="h-4 w-4 mr-1" />
              Generuoti teoriją
            </Link>
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && preview && (
        <>
          <Warnings preview={preview} />

          {/* Nothing to put beside it (an untagged skill with coverage hidden) means
              the theory gets the whole width rather than two thirds and a gap. */}
          <div className={`grid gap-4 ${hasSidePanel ? "lg:grid-cols-3" : ""}`}>
            <Card className={hasSidePanel ? "lg:col-span-2" : ""}>
              <CardContent className="pt-4">
                {preview.hasTheory && annotated ? (
                  <TheoryView
                    html={annotated}
                    sourceOrder
                    className={preview.tagged ? "theory-tags" : ""}
                  />
                ) : (
                  <div className="py-10 text-center space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Ši tema teorijos dar neturi, todėl „Parodyk pavyzdį“ neturi ko
                      parodyti.
                    </p>
                    <Button asChild size="sm">
                      <Link to={generateHref}>
                        <Wand2 className="h-4 w-4 mr-1" />
                        Generuoti teoriją
                      </Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <CoveragePanel preview={preview} showCoverage={showCoverage} />
              {showCoverage && <LevelsPanel levels={preview.levels} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * What is wrong, in one place and in plain words. Every item here is a child
 * meeting work nobody prepared them for, or reading an explanation nothing
 * practises.
 */
function Warnings({ preview }: { preview: TheoryPreview }) {
  if (!preview.hasTheory) return null;

  const untaught = preview.concepts.filter((c) => c.theoryBlocks === 0);
  const unexampled = preview.concepts.filter((c) => preview.tagged && !c.hasExample);
  const unpractised = preview.concepts.filter((c) => c.exerciseTotal === 0);
  const unintroduced = preview.concepts.filter((c) => !c.hasIntro);
  const untagged = preview.levels.reduce((sum, level) => sum + level.untagged, 0);

  const items: string[] = [];
  if (!preview.tagged && preview.concepts.length > 0) {
    items.push(
      "Teorija be žymių — parašyta prieš žymėjimą, todėl nežinome, kuri jos dalis " +
        "ką moko. Sugeneruok iš naujo, ir žymės atsiras.",
    );
  }
  if (preview.concepts.length === 0) {
    items.push(
      "Ši tema sąvokų sąrašo (concepts.json) dar neturi — sugeneruok teoriją, ir " +
        "sąrašas atsiras iš jos.",
    );
  }
  if (untaught.length > 0) {
    items.push(
      `Teorija nepaaiškina: ${untaught.map((c) => c.name).join(", ")}. Užduočių yra, ` +
        "paaiškinimo — ne.",
    );
  }
  if (unexampled.length > 0) {
    items.push(
      `Be išspręsto pavyzdžio: ${unexampled.map((c) => c.name).join(", ")}. Užstrigęs ` +
        "vaikas gaus svetimos sąvokos pavyzdį.",
    );
  }
  if (unpractised.length > 0) {
    items.push(
      `Nė vienos užduoties: ${unpractised.map((c) => c.name).join(", ")}. Teorija moko, ` +
        "o niekas netreniruoja.",
    );
  }
  if (untagged > 0 && preview.concepts.length > 0) {
    items.push(
      `${untagged} užduotis (-ys) be sąvokos — teorija jų neapima arba jos dar ` +
        "nepažymėtos.",
    );
  }
  // Last in the list, because unlike everything above it the child is not left
  // without anything: they get the derived introduction instead. It is here at
  // all because it is the only gap that is otherwise invisible — no screen, no
  // flag and no failing session distinguishes a written introduction from a
  // quoted line of theory.
  if (unintroduced.length > 0) {
    items.push(
      `Be įvadinio klausimo: ${unintroduced.map((c) => c.name).join(", ")}. Vaikas ` +
        "gauna silpnesnį, iš teorijos ištrauktą įvadą. Užpildoma: " +
        `rake generate:concept_intros SKILL=${preview.skillKey}`,
    );
  }

  if (items.length === 0) {
    return (
      <Card className="border-green-500/40 py-0">
        <CardContent className="py-3 text-sm text-green-700 dark:text-green-400">
          Kiekvieną sąvoką teorija paaiškina, kiekviena turi pavyzdį, įvadinį klausimą
          ir užduočių.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-500/40 py-0">
      <CardContent className="py-3 space-y-1.5">
        {items.map((item) => (
          <div key={item} className="flex gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <span>{item}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Both sides of every concept, side by side. */
function CoveragePanel({
  preview,
  showCoverage,
}: {
  preview: TheoryPreview;
  showCoverage: boolean;
}) {
  if (preview.concepts.length === 0) return null;

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">Sąvokos</h2>
          <span className="text-xs text-muted-foreground">
            ({preview.concepts.length})
          </span>
        </div>

        {preview.concepts.map((concept) => (
          <div key={concept.key} className="space-y-1 border-t pt-2 first:border-t-0 first:pt-0">
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium">{concept.name}</span>
              <div className="flex items-center gap-1 shrink-0">
                {concept.theoryBlocks === null ? (
                  <Badge variant="outline" className="text-[10px]">
                    žymių nėra
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      concept.theoryBlocks === 0
                        ? "text-amber-600 dark:text-amber-400 border-amber-400/50"
                        : ""
                    }`}
                    title="Teorijos dalys, kurios šią sąvoką paaiškina"
                  >
                    teorija {concept.theoryBlocks}
                  </Badge>
                )}
                {preview.tagged && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      concept.hasExample
                        ? ""
                        : "text-amber-600 dark:text-amber-400 border-amber-400/50"
                    }`}
                    title="Ar sąvoką moko išspręstas pavyzdys („Parodyk pavyzdį“)"
                  >
                    {concept.hasExample ? "pavyzdys ✓" : "be pavyzdžio"}
                  </Badge>
                )}
                {/* Not gated on `tagged`, unlike the example badge: an
                    introduction is a block of its own, so a theory can carry one
                    whether or not its prose is tagged. */}
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    concept.hasIntro
                      ? ""
                      : "text-amber-600 dark:text-amber-400 border-amber-400/50"
                  }`}
                  title={
                    concept.hasIntro
                      ? "Sąvoką atidaro parašytas įvadinis klausimas"
                      : "Įvadinio klausimo nėra — vaikas gaus silpnesnį, iš teorijos ištrauktą"
                  }
                >
                  {concept.hasIntro ? "įvadas ✓" : "be įvado"}
                </Badge>
              </div>
            </div>

            {concept.description && (
              <p className="text-xs text-muted-foreground">{concept.description}</p>
            )}

            {showCoverage && (
              <div className="flex items-center gap-3 text-xs">
                {concept.exerciseCounts.map((entry) => (
                  <span
                    key={entry.level}
                    className={
                      entry.count === 0
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                    }
                  >
                    L{entry.level} <span className="font-mono">{entry.count}</span>
                  </span>
                ))}
                <span className="ml-auto text-muted-foreground">
                  iš viso <span className="font-mono">{concept.exerciseTotal}</span>
                </span>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** How much of each level's work the theory actually claims. */
function LevelsPanel({ levels }: { levels: LevelCoverage[] }) {
  return (
    <Card>
      <CardContent className="pt-4 space-y-2">
        <h2 className="text-sm font-medium">Užduotys pagal lygį</h2>
        {levels.map((level) => (
          <div key={level.level} className="flex items-center gap-2 text-xs">
            <span className="w-6 font-medium">L{level.level}</span>
            <span className="text-muted-foreground">
              <span className="font-mono">{level.questions}</span> užd.
            </span>
            {level.questions > 0 && (
              <span
                className={
                  level.untagged > 0
                    ? "ml-auto text-amber-600 dark:text-amber-400"
                    : "ml-auto text-muted-foreground"
                }
              >
                {level.untagged > 0
                  ? `${level.untagged} be sąvokos`
                  : "visos pažymėtos"}
              </span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
